import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useNotificationStore } from './useNotificationStore';
import { logger } from '@/lib/logger';

export const useCompanyStore = create(
  devtools(
    (set, get) => ({
      // Estado inicial
      // NOTE: companies array removed - now managed by React Query (Section 19.1)
      // Only UI state remains here: activeCompany selection
      activeCompany: null,
      isLoading: false,
      error: null,
      lastCompanyChange: null, // Timestamp para forzar re-renders
      isSwitching: false, // [INBEST:addon] START - Estado para cambio de empresa

      // Acciones
      // setCompanies removed - companies come from React Query hook
      setActiveCompany: (company) => {
        const currentCompany = get().activeCompany;
        // Solo actualizar si realmente cambió la empresa
        if (!currentCompany || currentCompany.id !== company?.id) {
          set({ 
            activeCompany: company,
            lastCompanyChange: Date.now() // Forzar actualización
          });
        }
      },
      setError: (error) => set({ error, isLoading: false }),

      // [INBEST:addon] START - Acciones para empresa activa
      // NOTE: setActiveCompanyId now requires companies array parameter
      // Companies come from React Query hook, not from store
      setActiveCompanyId: (companyId, companies = []) => {
        const { activeCompany } = get();
        
        // CRITICAL: Guard against redundant updates - no-op if already set to same company
        if (activeCompany && activeCompany.id === companyId) {
          return; // Already set to this company, no update needed
        }
        
        const company = companies.find((c) => c.id === companyId);
        if (company) {
          set({ 
            activeCompany: company,
            lastCompanyChange: Date.now()
          });
        }
      },

      persistActiveCompany: (companyId) => {
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('activeCompanyId', companyId.toString());
          }
        } catch (error) {
          if (import.meta.env.DEV) {
            logger.warn('No se pudo guardar empresa activa en localStorage:', error);
          }
        }
      },

      // NOTE: hydrateActiveCompany now requires companies array parameter
      // Companies come from React Query hook
      // Loads active company from localStorage (no backend call)
      hydrateActiveCompany: (companies = []) => {
        try {
          // SSR-safe: localStorage only available in browser
          if (typeof window === 'undefined') {
            return;
          }

          const { activeCompany } = get();
          const savedId = localStorage.getItem('activeCompanyId');
          
          // CRITICAL: Guard against redundant updates - no-op if already set to same company
          if (activeCompany && savedId && activeCompany.id === Number(savedId)) {
            return; // Already set to saved company, no update needed
          }
          
          if (savedId) {
            const { setActiveCompanyId } = get();
            const company = companies.find((c) => c.id === Number(savedId));
            if (company) {
              setActiveCompanyId(company.id, companies);
            }
          }
        } catch (error) {
          logger.warn('Error restaurando empresa activa desde localStorage:', error);
          // No lanzar error - continuar sin empresa activa si localStorage falla
        }
      },

      refreshAllData: () => {
        // Disparar recarga global de datos
        set({ lastCompanyChange: Date.now() });
      },

      setSwitching: (isSwitching) => set({ isSwitching }),
      // [INBEST:addon] END

      // NOTE: fetchUserCompanies removed - use React Query hook instead (Section 19.1, 19.2)
      // Companies are now fetched via useCompanies() hook from @/hooks/queries/useCompanies
      // This store only manages UI state (activeCompany selection)

      // Acción para establecer la empresa activa con notificación de cambio
      // NOTE: Now requires companies array parameter (from React Query)
      setActiveCompanyById: (companyId, companies = []) => {
        const { activeCompany } = get();
        const company = companies.find((c) => c.id === companyId);
        
        if (company && (!activeCompany || activeCompany.id !== companyId)) {
          const previousCompany = activeCompany;
          set({ 
            activeCompany: company,
            lastCompanyChange: Date.now() // Forzar actualización
          });
          
          // Notificar cambio para debugging
          const { setNotification } = useNotificationStore.getState();
          setNotification(`Empresa activa cambiada a: ${company.name}`, 'success');
          
          return company;
        } else if (!company) {
          logger.warn('Empresa no encontrada con ID:', companyId);
          return null;
        } else {
          return company;
        }
      },

      // Acción para establecer la primera empresa como activa por defecto
      // NOTE: Now requires companies array parameter (from React Query)
      setDefaultActiveCompany: (companies = []) => {
        const { activeCompany } = get();
        if (companies.length > 0 && !activeCompany) {
          const defaultCompany = companies[0];
          set({ 
            activeCompany: defaultCompany,
            lastCompanyChange: Date.now()
          });
          return defaultCompany;
        }
        return null;
      },

      // NOTE: refreshCompanies removed - use React Query refetch instead

      // Getters computados
      getActiveCompanyId: () => {
        const { activeCompany } = get();
        return activeCompany?.id || null;
      },

      getActiveCompanyName: () => {
        const { activeCompany } = get();
        return activeCompany?.name || 'Sin empresa seleccionada';
      },

      // NOTE: hasCompanies removed - check companies.length from React Query hook instead

      // NOTE: initializeCompanyStore removed - use React Query hook instead
      // Companies are automatically fetched when authenticated

      // Acción para forzar actualización de datos
      forceDataRefresh: () => {
        set({ lastCompanyChange: Date.now() });
      }
    }),
    {
      name: 'company-store',
    }
  )
);
